import { Injectable, inject } from "@angular/core";
import { ReportDto, ReportViewModel } from "../../models/dtos/ReportDto";
import { BehaviorSubject, defaultIfEmpty, delay, map, Observable, Subject, switchMap, tap } from "rxjs";
import { ReportService } from "../../data/report.service";
import { TranslateService } from "@ngx-translate/core";
import { ReportTypeEnum } from "../../models/enums/report-type";

@Injectable({
  providedIn: 'root'
})
export class ReportsListDataSource {
  private reportService = inject(ReportService);
  private translateService = inject(TranslateService);

  countSubject = new BehaviorSubject<number>(0);
  count$: Observable<number> = this.countSubject.asObservable();

  constructor() { }

  reportsSubject = new Subject<PagingRequest>();
  reports$: Observable<ReportViewModel[]> = this.reportsSubject.pipe(
    delay(0),
    switchMap((request) =>
      this.reportService.getReports(request.Page, request.PageSize, request.SortingField, request.SortingDirection).pipe(
        tap((res) => this.countSubject.next(res.Count)),
        map((res) => {
          const reports = res.Results;

          if (!reports || reports.length === 0) {
            return [];
          }

          const newestReport = reports.reduce((newest, report) => {
            return new Date(report.GeneratedDate) > new Date(newest.GeneratedDate) ? report : newest;
          }, reports[0]);

          return reports.map(report => ({
            ...report,
            IsNewest: report === newestReport,
            DisplayName: this.computeDisplayName(report),
          } as ReportViewModel));
        }),
        defaultIfEmpty([])
      ),
    ),
  );

  private computeDisplayName(report: ReportDto): string {
    if (report.ReportTypeId === ReportTypeEnum.ActiveEmployeesReport && report.AdditionalData) {
      try {
        const data = JSON.parse(report.AdditionalData);
        if (data?.allInstitution) {
          const suffix = this.translateService.instant('Reports.AllInstitutionSuffix');
          return `${report.Name} (${suffix})`;
        }
      } catch { }
    }
    return report.Name;
  }
}

interface PagingRequest {
  Page: number;
  PageSize: number;
  SortingDirection: string;
  SortingField: string;
}
